/**
 * resolveLearnerHome — single source of truth for "where is the learner home?"
 *
 * Issue #1521 + #1627 (messenger shell, epic #1518). #1627 split the flag:
 * `branding.messenger_is_home === true` makes the learner home /messenger;
 * `messenger_enabled` only makes /messenger REACHABLE (route works, no forced
 * redirect) and does NOT move the home. So the pilot config
 * (messenger_enabled=true, messenger_is_home=false) keeps /dashboard as home
 * while /messenger is reachable directly. For everyone else home stays
 * /dashboard. Every callsite that used to hardcode navigate('/dashboard') as a
 * "go home" action must resolve the target through this helper — per-callsite
 * conditionals are how the old tour ended up hard-redirecting into the wrong
 * home (spec Removal Impact).
 *
 * `/dashboard` remains directly routable as an escape hatch (U1): this
 * helper changes only the DEFAULT home target, never reachability.
 *
 * The old org-dashboard escape-hatch navigation state is gone (#2915: the
 * facilitator redirect into /org-dashboard that it escaped was removed, so
 * nothing read it; #4227: the callsites that still passed it were cleaned up).
 * Nothing passes it any more and nothing reads it.
 *
 * This module is deliberately context-free (no React imports) so plain
 * modules and node-env tests can use it. Components use the `useLearnerHome`
 * hook in ./useLearnerHome.js.
 */

export const MESSENGER_HOME = '/messenger';
export const DASHBOARD_HOME = '/dashboard';

/**
 * Pure resolver. Accepts either the BrandingContext value ({messengerIsHome})
 * or a raw branding payload ({messenger_is_home}) so it is usable both inside
 * components and in plain modules. Keys on the HOME flag, not reachability —
 * `messenger_enabled` alone never moves the home (#1627).
 *
 * @param {object|null|undefined} branding
 * @returns {string} '/messenger' | '/dashboard'
 */
export function resolveLearnerHome(branding) {
  const isHome =
    branding?.messengerIsHome === true || branding?.messenger_is_home === true;
  return isHome ? MESSENGER_HOME : DASHBOARD_HOME;
}
